import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  nuloSeNaoEncontrado,
  type PaginaComPermissoes,
} from '@/modulos/operacao/cadastros/leitura';
import { parametrosDaListagem } from '@/modulos/operacao/comum/contrato';
import { naoInsistirEmRecusa } from '@/modulos/operacao/comum/dados';
import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { exportarCsv } from '@/nucleo/exportarCsv';

import {
  documentoDoTitular,
  naturezaDoTitular,
  type FiltroDeTitulares,
  type Titular,
  type TitularDetalhe,
} from './contrato';

/**
 * A camada de dados dos titulares, lida da API (`apps/api/src/modulos/titulares`).
 *
 * NAO HA MUTACAO NENHUMA: titular chega por integracao com o SGTP, e o TEG le. Um
 * gancho de gravacao neste arquivo seria porta para uma tela de cadastro que nao
 * deve existir.
 *
 * O RECORTE E DO BANCO (0012): a diretoria ve o titular VIGENTE de veiculo de
 * credenciado da propria DRE. Titular sem veiculo no recorte nao aparece para
 * ela, e isso nao e falha da tela.
 */

export const CAMINHO_DA_API = '/bff/v1/titulares';

export const CHAVES_DE_TITULARES = {
  lista: (filtro: FiltroDeTitulares) => ['titulares', 'lista', filtro] as const,
  exportacao: (filtro: Omit<FiltroDeTitulares, 'pagina' | 'tamanho'>) =>
    ['titulares', 'exportacao', filtro] as const,
  detalhe: (slug: string) => ['titulares', 'detalhe', slug] as const,
};

/** Teto da exportacao: o mesmo da API (`TETO_DA_EXPORTACAO`). */
export const TETO_DA_EXPORTACAO = 1000;

/**
 * Lista paginada no servidor. Tambem alimenta o `TitularCombobox` do formulario de
 * veiculo, com a mesma assinatura.
 */
export function useTitulares(
  filtro: FiltroDeTitulares,
): UseQueryResult<PaginaComPermissoes<Titular>, ErroApi> {
  return useQuery<PaginaComPermissoes<Titular>, ErroApi>({
    queryKey: CHAVES_DE_TITULARES.lista(filtro),
    queryFn: ({ signal }) =>
      chamarApi<PaginaComPermissoes<Titular>>(
        `${CAMINHO_DA_API}${parametrosDaListagem({ ...filtro })}`,
        { sinal: signal },
      ),
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * O titular de `/operacao/titulares/:slug`, com os veiculos vinculados.
 *
 * NUNCA LANÇA por 404: um titular que nao existe, ou que o recorte nao mostra,
 * vira `null` e a tela diz "Titular nao encontrado" — link quebrado nao e falha de
 * sistema.
 */
export function useTitular(
  slug: string | undefined,
): UseQueryResult<TitularDetalhe | null, ErroApi> {
  return useQuery<TitularDetalhe | null, ErroApi>({
    queryKey: CHAVES_DE_TITULARES.detalhe(slug ?? ''),
    enabled: !!slug,
    queryFn: ({ signal }) =>
      chamarApi<TitularDetalhe>(`${CAMINHO_DA_API}/${encodeURIComponent(slug ?? '')}`, {
        sinal: signal,
      }).catch(nuloSeNaoEncontrado),
    retry: naoInsistirEmRecusa,
  });
}

/** Baixa a listagem filtrada como CSV, pela rota de exportacao, que registra o acesso. */
export function useBaixarTitularesCsv(): (filtro: FiltroDeTitulares) => Promise<number> {
  const consultas = useQueryClient();

  return async ({ pagina: _pagina, tamanho: _tamanho, ...recorte }: FiltroDeTitulares) => {
    const resposta = await consultas.fetchQuery<PaginaComPermissoes<Titular>>({
      queryKey: CHAVES_DE_TITULARES.exportacao(recorte),
      queryFn: () =>
        chamarApi<PaginaComPermissoes<Titular>>(
          `${CAMINHO_DA_API}/exportacao${parametrosDaListagem({ ...recorte })}`,
        ),
      staleTime: 0,
    });

    exportarCsv(
      'titulares.csv',
      ['Nome', 'CPF/CNPJ', 'PF/PJ', 'Origem', 'Conflito', 'Situação'],
      resposta.itens.map((titular) => [
        titular.nome,
        documentoDoTitular(titular) ?? '',
        naturezaDoTitular(titular) ?? '',
        titular.origem_dado ?? '',
        titular.conflito_integracao ? 'Sim' : 'Não',
        titular.status,
      ]),
    );

    return resposta.itens.length;
  };
}
